<template>
  <FormContainer>
    <template #title>Adresse du lieu de formation</template>
    <template #content>
      <div class="form__bloc">
        <BasicCheckbox
          v-if="metaModuleEnCours?.lieuFormationIdentiqueCfaPrincipal?.$status !== PropertyStatus.NotPresent"
          id="field_26"
          :model-value="moduleEnCours?.lieuFormationIdentiqueCfaPrincipal"
          @update:model-value="(value) => setAdresseIdentiqueCfaPrincipal(value!)"
          label="La formation a lieu à l'adresse de l'organisme de formation"
          class="col-36"
          :readonly
        />
        <!-- Plus besoin de ce cas-->
        <BasicCheckbox
          v-else
          id="field_66"
          label="La formation a lieu à l'adresse de l'organisme de formation"
          class="col-36"
          disabled
        />
      </div>
      <div
        class="form__bloc"
        v-if="metaModuleEnCours?.lieuFormation?.$status !== PropertyStatus.NotPresent"
      >
        <WrapperInput
          id="field_27 field_67"
          label="N° et libellé de la voie (adresse)"
          class="col-12 col-md-18 col-sm-36"
          @update:model-value="(value: any) => updateModule('lieuFormation.voie', activeModuleIndex!, value)"
          :model-value="moduleEnCours?.lieuFormation?.voie"
          :required="metaModuleEnCours?.lieuFormation?.voie?.$status === PropertyStatus.Required"
          :alertLabel="
            metaModuleEnCours?.lieuFormation?.voie?.$messageErreurDirty ??
            metaModuleEnCours?.lieuFormation?.$messageErreurDirty
          "
          :maxlength="38"
          :readonly
          deletable
        />
        <WrapperInput
          id="field_unknown"
          :model-value="moduleEnCours?.lieuFormation?.complement"
          @update:model-value="(value: any) => updateModule('lieuFormation.complement', activeModuleIndex!, value)"
          label="Complément d'adresse"
          class="col-12 col-md-18 col-sm-36"
          :required="
            metaModuleEnCours?.etablissementPrestataire?.adresse?.complement?.$status === PropertyStatus.Required
          "
          :alertLabel="metaModuleEnCours?.etablissementPrestataire?.adresse?.complement?.$messageErreurDirty"
          :maxlength="38"
          :readonly
          deletable
        />
        <WrapperAutocomplete
          id="field_28 field_68"
          key-id="codeInsee"
          key-label="intitule"
          :search="searchCommune"
          v-model="codePostalModel"
          :get-item-label="getCpCommuneLabel"
          label="Code postal"
          :search-min-length="3"
          :placeholder="PLACEHOLDER_ADR_CP"
          :readonly
          deletable
          class="col-4 col-md-6 col-sm-36"
          :strict="false"
          :required="metaModuleEnCours?.lieuFormation?.codePostal?.$status === PropertyStatus.Required"
          :alertLabel="
            metaModuleEnCours?.lieuFormation?.codePostal?.$messageErreurDirty ??
            metaModuleEnCours?.lieuFormation?.$messageErreurDirty
          "
          :maxlength="5"
        />
        <WrapperAutocomplete
          id="field_29 field_69"
          key-id="codeInsee"
          key-label="intitule"
          :search="searchCommune"
          v-model="communeModel"
          :get-item-label="getCpCommuneLabel"
          label="Commune"
          :search-min-length="3"
          :placeholder="PLACEHOLDER_ADR_VILLE"
          :readonly
          deletable
          class="col-8 col-md-12 col-sm-36"
          :strict="false"
          :required="metaModuleEnCours?.lieuFormation?.commune?.$status === PropertyStatus.Required"
          :alertLabel="
            metaModuleEnCours?.lieuFormation?.commune?.$messageErreurDirty ??
            metaModuleEnCours?.lieuFormation?.$messageErreurDirty
          "
          :maxlength="50"
        />
      </div>
      <div
        v-else
        class="form__bloc"
      >
        <WrapperInput
          id="field_27 field_67"
          label="N° et libellé de la voie (adresse)"
          class="col-12 col-md-18 col-sm-36"
          :maxlength="38"
          disabled
        />
        <WrapperInput
          id="field_21"
          label="Complément d'adresse"
          class="col-12 col-md-18 col-sm-36"
          :maxlength="38"
          disabled
        />
        <WrapperInput
          id="field_28 field_68"
          label="Code postal"
          class="col-4 col-md-6 col-sm-36"
          :maxlength="5"
          disabled
        />
        <WrapperInput
          id="field_29 field_69"
          label="Ville"
          class="col-8 col-md-12 col-sm-36"
          :maxlength="50"
          disabled
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import type { ModuleDTO } from '@/features/api/extranet/dto/Demande/CXPI'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus.ts'
  import {
    type CodePostalCommuneValue,
    useCodePostalCommune,
  } from '@/features/interface/composables/useCodePostalCommune'
  import { CXPI_MODULES_ACTIONS } from '@/features/interface/demande/cxpi/steps/formation/useModuleActions.ts'
  import { CXPI_ACTIONS } from '@/features/interface/demande/cxpi/useDemandeCXPIActions'
  import { DEMANDE_STORE, FormContainer } from '@/features/interface/demande/shared'
  import { PLACEHOLDER_ADR_CP, PLACEHOLDER_ADR_VILLE } from '@/features/interface/demande/shared'
  import { storeToRefs } from 'pinia'
  import { inject } from 'vue'
  import type { AdresseDTO } from '@/features/api/extranet/dto/shared'

  const store = inject(DEMANDE_STORE)!.getStore<'MODEL_CXPI'>()
  const { setupArrayUpdaterWithKey, setupCustomUpdater } = store
  const { readonly } = storeToRefs(store)
  const { moduleEnCours, activeModuleIndex, metaModuleEnCours, setAdresseIdentiqueCfaPrincipal } =
    inject(CXPI_MODULES_ACTIONS)!
  const { searchCommune } = inject(CXPI_ACTIONS)!

  const { codePostalModel, communeModel, getCpCommuneLabel } = useCodePostalCommune({
    getCodePostal: () => moduleEnCours?.value?.lieuFormation?.codePostal,
    getCommune: () => moduleEnCours?.value?.lieuFormation?.commune,
    updateValues: setupCustomUpdater<CodePostalCommuneValue>((patch, value) => {
      const module = patch.formation!.modules![activeModuleIndex.value!]!
      if (!module.lieuFormation) {
        module.lieuFormation = {} as AdresseDTO
      }
      module.lieuFormation.codePostal = value.code ?? moduleEnCours!.value!.lieuFormation?.codePostal ?? null
      module.lieuFormation.commune = value.commune ?? moduleEnCours!.value!.lieuFormation?.commune ?? null
    }),
  })

  const updateModule = setupArrayUpdaterWithKey<ModuleDTO>('formation.modules')
</script>
